import { smoothScrollIntoView } from '@domgen/base-design-system-2.0/animation/smoothScrollIntoView';
import { Footer } from '@domgen/base-design-system-2.0/atoms/Footer';
import { HeaderCobranded } from '@domgen/base-design-system-2.0/atoms/HeaderCobranded';
import { isCallUsHeaderPage, PosthogFeatureFlags } from '@domgen/shared/enums/featureFlags';
import { usePostHog } from '@posthog/react';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { CallUsBanner } from './components/CallUsBanner';
import { useAppContext } from './context/AppContext';
import { AppRoute } from './enums/appRoutes';
import { useIsTenantFeatureEnabled } from './hooks';
import { pageviewTracker } from './services/posthog/pageviewTracker';
import { usePageviewLocation } from './services/posthog/usePageviewLocation';
import { store } from './store/store';

/**
 * Routes that use the SEO-optimised page title rather than the default "Registration - {route}" title.
 * These pages show the same content, so they also share a canonical URL pointing at the brand's root.
 */
const SEO_ROUTES = [AppRoute.LANDING, AppRoute.APPLIANCE_DETAILS];

export const Layout = () => {
  const { t } = useTranslation('common', {
    keyPrefix: 'footer',
  });
  const location = useLocation();
  const { clientLogo, dGLogo, getRouteFromPath } = useAppContext();
  const isApplianceCareTermAndConditionsDisplayed = useIsTenantFeatureEnabled(
    'applianceCareTermAndConditions',
  );
  const clientName = import.meta.env.VITE_CLIENT_FRIENDLY_NAME.trim();
  const navigate = useNavigate();
  const posthog = usePostHog();
  const pageviewLocation = usePageviewLocation();
  const callUsHeaderEnabled =
    isCallUsHeaderPage(
      posthog.getFeatureFlagResult(PosthogFeatureFlags.CALL_US_HEADER, { send_event: false })
        ?.payload,
      location.pathname,
      import.meta.env.VITE_BRAND_CODE?.trim(),
    ) && posthog.getFeatureFlag(PosthogFeatureFlags.CALL_US_HEADER) === 'test';

  const links = [
    ...(isApplianceCareTermAndConditionsDisplayed
      ? [
          {
            text: t('links.applianceCareTermsAndConditions.text'),
            href: t('links.applianceCareTermsAndConditions.href'),
          },
        ]
      : []),
    {
      text: t('links.productSupport.text', { clientName }),
      href: t('links.productSupport.href', { clientName: clientName.toLowerCase() }),
    },
    {
      text: t('links.termsOfUse.text'),
      href: t('links.termsOfUse.href'),
    },
    {
      text: t('links.domesticAndGeneralPrivacyNotice.text'),
      href: t('links.domesticAndGeneralPrivacyNotice.href'),
    },
    {
      text: t('links.clientPrivacyNotice.text', { clientName }),
      href: t('links.clientPrivacyNotice.href'),
    },
    {
      text: t('links.cookiesNotice.text'),
      href: t('links.cookiesNotice.href'),
    },
  ];

  useEffect(() => {
    smoothScrollIntoView(0);
  }, [location.pathname]);

  // A visit to a pathname gets one PostHog $pageview, sent by TrackedPage once the page renders, or
  // by the tracker when the visit is left without its page rendering (a redirect, an abandoned loader)
  useEffect(() => {
    pageviewTracker.startVisit(pageviewLocation);
  }, [pageviewLocation.pathname]);

  /** This is where all the navigation events are handled */
  useEffect(() => {
    const sub = store.on('navigate', ({ path, replace }) => {
      navigate(path, { replace });
    });
    return sub.unsubscribe;
  }, [navigate]);

  const isSeoRoute = SEO_ROUTES.includes(location.pathname as AppRoute);
  const canonicalUrl = `https://register.${clientName.toLowerCase()}.com/`;

  const getPageTitle = () => {
    if (isSeoRoute) {
      return `Register Your ${clientName} Appliance | Warranty Signup`;
    }
    const route = getRouteFromPath(location.pathname);
    if (!route) return `${clientName} Registration`;
    return `${clientName} Registration - ${route.title}`;
  };

  return (
    <div className="flex flex-col min-h-screen">
      <HeaderCobranded
        clientIcon={<NavLink to="/">{clientLogo}</NavLink>}
        dgLogo={dGLogo}
        clientName={clientName}
        actionSlot={callUsHeaderEnabled ? <CallUsBanner /> : undefined}
      />
      <main className="grow flex flex-col">
        <title>{getPageTitle()}</title>
        {isSeoRoute && <link rel="canonical" href={canonicalUrl} />}
        <Outlet />
      </main>
      <Footer links={links} disclaimer={t('disclaimer')} />
    </div>
  );
};
